import { useMutation } from '@apollo/client';
import type { DeleteInternalAuditsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteInternalAuditsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteInternalAuditsTRPC } from './useDeleteInternalAuditsTRPC';

export const useDeleteInternalAudits = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteInternalAuditsGraphQL, graphqlState] = useMutation(
    DeleteInternalAuditsDocument,
    {
      update: (cache) => {
        evictField(cache, 'internal_audit_entity');
        evictField(cache, 'internal_audit_entity_aggregate');
      },
      fetchPolicy: 'no-cache',
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteInternalAuditsTRPC();

  const deleteInternalAudits = async (
    ids: string[]
  ): Promise<DeleteInternalAuditsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteInternalAudits(ids);
    }

    const result = await deleteInternalAuditsGraphQL({
      variables: { Ids: ids },
    });
    if (!result.data) {
      throw new Error('Failed to delete internal audits');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteInternalAudits,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteInternalAudits,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
